import React, { createContext, useContext, useState, useCallback } from "react";
import { Alert, Snackbar } from "@mui/material";
import { getErrorMessage } from "../utils/utils";

type Severity = "success" | "error" | "info" | "warning";

interface NotificationContextValue {
  showSnackbar: (message: string, severity: Severity) => void;
  withErrorNotification: <TArgs extends unknown[], TReturn>(
    fn: (...args: TArgs) => Promise<TReturn>,
    errorMessage?: string,
  ) => (...args: TArgs) => Promise<TReturn | undefined>;
}

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<Severity>("info");

  const showSnackbar = useCallback(
    (message: string, severity: Severity) => {
      setMessage(message);
      setSeverity(severity);
      setOpen(true);
    },
    [],
  );

  const withErrorNotification = useCallback(
    <TArgs extends unknown[], TReturn>(
      fn: (...args: TArgs) => Promise<TReturn>,
      errorMessage?: string,
    ) => {
      return async (...args: TArgs): Promise<TReturn | undefined> => {
        try {
          return await fn(...args);
        } catch (err) {
          const message = errorMessage ?? getErrorMessage(err);
          showSnackbar(message, "error");
          return undefined;
        }
      };
    },
    [showSnackbar],
  );

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <NotificationContext.Provider value={{ showSnackbar, withErrorNotification }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
}
